/**
 * Per-country configuration for the registers FirmenData covers. Every
 * "which country is this company" decision goes through `companyCountryCode`
 * and every country-dependent label, region list or availability flag lives
 * in `COMPANY_COUNTRIES` — never test `register_type !== 'CH-HR'` to mean
 * "German": an Austrian (AT-FB) row would fall into that branch.
 */

/** The countries whose registers FirmenData covers (ISO-3166-1 alpha-2). */
export type CompanyCountryCode = 'DE' | 'CH' | 'AT';

/** Swiss canton code (what `register_court` stores for CH-HR rows) → the
 *  canton's own official name (Vaud, not the German exonym Waadt), so the
 *  URL's court segment reads like a place ("vaud"), the way German URLs
 *  carry the Amtsgericht city ("stuttgart"). The backend's by-slug lookup
 *  translates the name slug back to the code
 *  (backend/zefix/utils.canton_code_for_slug). */
export const SWISS_CANTON_NAMES: Record<string, string> = {
  AG: 'Aargau',
  AI: 'Appenzell Innerrhoden',
  AR: 'Appenzell Ausserrhoden',
  BE: 'Bern',
  BL: 'Basel-Landschaft',
  BS: 'Basel-Stadt',
  FR: 'Fribourg',
  GE: 'Genève',
  GL: 'Glarus',
  GR: 'Graubünden',
  JU: 'Jura',
  LU: 'Luzern',
  NE: 'Neuchâtel',
  NW: 'Nidwalden',
  OW: 'Obwalden',
  SG: 'St. Gallen',
  SH: 'Schaffhausen',
  SO: 'Solothurn',
  SZ: 'Schwyz',
  TG: 'Thurgau',
  TI: 'Ticino',
  UR: 'Uri',
  VD: 'Vaud',
  VS: 'Valais',
  ZG: 'Zug',
  ZH: 'Zürich',
};

/** The 16 German Bundesländer, spelled as `mv_search.bundesland` stores them. */
export const GERMAN_REGION_NAMES = [
  'Baden-Württemberg',
  'Bayern',
  'Berlin',
  'Brandenburg',
  'Bremen',
  'Hamburg',
  'Hessen',
  'Mecklenburg-Vorpommern',
  'Niedersachsen',
  'Nordrhein-Westfalen',
  'Rheinland-Pfalz',
  'Saarland',
  'Sachsen',
  'Sachsen-Anhalt',
  'Schleswig-Holstein',
  'Thüringen',
];

/** The 9 Austrian Bundesländer, spelled as the backend stores them in
 *  `bundesland` for AT-FB rows (backend/firmenbuch/regions.py). */
export const AUSTRIAN_REGION_NAMES = [
  'Burgenland',
  'Kärnten',
  'Niederösterreich',
  'Oberösterreich',
  'Salzburg',
  'Steiermark',
  'Tirol',
  'Vorarlberg',
  'Wien',
];

interface CompanyCountryConfig {
  /** i18n key of the country's name (flag tooltip, profile). */
  countryNameKey: string;
  /** i18n key of the country-scope picker label. */
  scopeLabelKey: string;
  /** Name of the register number (JSON-LD propertyID, column labels). */
  registerLabel: string;
  registerShortLabel: string;
  /** What keeps the register entry (JSON-LD register-authority propertyID). */
  courtLabel: string;
  /** Region vocabulary of the location filter (Bundesländer / cantons). */
  regions: string[];
  /** Register authority cited as JSON-LD sourceOrganization. */
  sourceOrganization: {
    '@type': 'GovernmentOrganization';
    name: string;
    alternateName?: string;
    url: string;
  };
  spatialCoverage: string;
  /** `source.origin` of the per-company dataset download. */
  datasetOrigin: string;
  /** German Handelsregister documents (AD/CD/DK) exist for this country —
   *  gates the register-docs card, its mobile action and the JSON-LD offers. */
  germanRegisterDocuments: boolean;
  /** Offered in the country-scope pickers of search / Insolvenzticker /
   *  Vergabe. AT is off until the Austrian backfill has landed; every code
   *  path must keep working when a flag flips to true. */
  searchScope: boolean;
  insolvencyScope: boolean;
  tenderScope: boolean;
}

export const COMPANY_COUNTRIES: Record<
  CompanyCountryCode,
  CompanyCountryConfig
> = {
  DE: {
    countryNameKey: 'companyDetail.countryName.DE',
    scopeLabelKey: 'search.sidebar.countryGermany',
    registerLabel: 'Handelsregisternummer',
    registerShortLabel: 'HR',
    courtLabel: 'Registergericht',
    regions: GERMAN_REGION_NAMES,
    sourceOrganization: {
      '@type': 'GovernmentOrganization',
      name: 'Handelsregister',
      url: 'https://www.handelsregister.de',
    },
    spatialCoverage: 'Germany',
    datasetOrigin: 'German Handelsregister / Unternehmensregister',
    germanRegisterDocuments: true,
    searchScope: true,
    insolvencyScope: true,
    tenderScope: true,
  },
  CH: {
    countryNameKey: 'companyDetail.countryName.CH',
    scopeLabelKey: 'search.sidebar.countrySwitzerland',
    registerLabel: 'UID',
    registerShortLabel: 'UID',
    courtLabel: 'Handelsregister (Kanton)',
    regions: Object.values(SWISS_CANTON_NAMES),
    sourceOrganization: {
      '@type': 'GovernmentOrganization',
      name: 'Zefix – Zentraler Firmenindex',
      alternateName: 'Schweizerisches Handelsregister',
      url: 'https://www.zefix.ch',
    },
    spatialCoverage: 'Switzerland',
    datasetOrigin: 'Swiss commercial register (Zefix) / Swiss Official Gazette of Commerce (SHAB)',
    germanRegisterDocuments: false,
    searchScope: true,
    insolvencyScope: true,
    tenderScope: true,
  },
  AT: {
    countryNameKey: 'companyDetail.countryName.AT',
    scopeLabelKey: 'search.sidebar.countryAustria',
    registerLabel: 'Firmenbuchnummer',
    registerShortLabel: 'FN',
    courtLabel: 'Firmenbuchgericht',
    regions: AUSTRIAN_REGION_NAMES,
    sourceOrganization: {
      '@type': 'GovernmentOrganization',
      name: 'Österreichisches Firmenbuch / BMJ – Justiz',
      url: 'https://justizonline.gv.at',
    },
    spatialCoverage: 'Austria',
    datasetOrigin: 'Austrian Firmenbuch (BMJ – Justiz), CC BY 4.0',
    germanRegisterDocuments: false,
    searchScope: false,
    insolvencyScope: false,
    tenderScope: false,
  },
};

export function isCompanyCountryCode(
  value: unknown,
): value is CompanyCountryCode {
  return typeof value === 'string' && Object.hasOwn(COMPANY_COUNTRIES, value);
}

/** ISO-3166-1 alpha-2 country of a company row: AT-FB rows (eu_id
 *  "ATBRA.…") are Austrian, CH-HR rows (eu_id "CH.…") Swiss, every other
 *  register type German. The eu_id prefix covers callers that lack the
 *  register type. */
export function companyCountryCode(
  registerType?: string | null,
  euId?: string | null,
): CompanyCountryCode {
  if (registerType === 'AT-FB' || euId?.startsWith('ATBRA.')) return 'AT';
  return registerType === 'CH-HR' || euId?.startsWith('CH.') ? 'CH' : 'DE';
}

/** Countries offered in a surface's country-scope picker, in config order. */
export function countryScopeOptions(
  surface: 'searchScope' | 'insolvencyScope' | 'tenderScope',
): CompanyCountryCode[] {
  return (Object.keys(COMPANY_COUNTRIES) as CompanyCountryCode[]).filter(
    (code) => COMPANY_COUNTRIES[code][surface],
  );
}

/** Picker label key for a scope value; '' (and anything unknown) is Global. */
export function countryScopeLabelKey(value: string): string {
  return isCompanyCountryCode(value)
    ? COMPANY_COUNTRIES[value].scopeLabelKey
    : 'search.sidebar.countryGlobal';
}
